import type { TextareaHTMLAttributes } from 'react';

import { css, styled } from 'styled-components';

import { FormControl, FormControlChildrenCSS } from '../FormControl';

const lightTextarea = css`
  background-color: ${(props) => props.theme.colors.slateGray[10]};
  color: ${(props) => props.theme.colors.oxfordBlue[100]};
  border: 2px solid ${(props) => props.theme.colors.slateGray[10]};

  &::placeholder {
    color: ${(props) => props.theme.colors.oxfordBlue[80]};
  }

  &:disabled {
    border: 1px solid ${(props) => props.theme.colors.slateGray[10]};
    &::placeholder {
      color: ${(props) => props.theme.colors.oxfordBlue[30]};
    }
  }
`;

const twilightTextarea = css`
  background-color: ${(props) => props.theme.colors.slateGray[10]};
  color: ${(props) => props.theme.colors.oxfordBlue[100]};
  border: 2px solid ${(props) => props.theme.colors.slateGray[10]};

  &::placeholder {
    color: ${(props) => props.theme.colors.oxfordBlue[80]};
  }

  &:disabled {
    background-color: inherit;
    border: 1px solid ${(props) => props.theme.colors.oxfordBlue[80]};
    &::placeholder {
      color: ${(props) => props.theme.colors.oxfordBlue[30]};
    }
  }
`;

const darkTextarea = css`
  background-color: inherit;
  color: ${(props) => props.theme.colors.white};
  border: 2px solid ${(props) => props.theme.colors.oxfordBlue[80]};

  &::placeholder {
    color: ${(props) => props.theme.colors.oxfordBlue[10]};
  }

  &:disabled {
    background-color: inherit;
    border: 1px solid ${(props) => props.theme.colors.oxfordBlue[80]};
    &::placeholder {
      color: ${(props) => props.theme.colors.oxfordBlue[80]};
    }
  }
`;

const TextareaSC = styled.textarea<{
  $colorVariant?: 'light' | 'dark' | 'twilight';
  $disableResize?: boolean;
  $isDisabled?: boolean;
  $isValid?: boolean;
}>`
  ${(props) => {
    switch (props.$colorVariant) {
      case 'dark':
        return darkTextarea;
      case 'twilight':
        return twilightTextarea;
      default:
        return lightTextarea;
    }
  }}

  ${FormControlChildrenCSS}

  ${(props) => props.$disableResize && `resize: none`}
`;

const FormControlSC = styled(FormControl)`
  height: 9.5rem;
`;

type Props = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  colorVariant?: 'light' | 'dark' | 'twilight';
  'data-test-id'?: string;
  disableResize?: boolean;
  hint?: string;
  isDisabled?: boolean;
  isValid?: boolean;
  label?: string;
  validationPassedLabel?: string;
  validationWarningLabel?: string;
};

export const Textarea = ({
  colorVariant,
  disableResize = true,
  hint,
  isDisabled,
  isValid,
  label,
  rows = 4,
  validationPassedLabel,
  validationWarningLabel,
  ...rest
}: Props) => {
  return (
    <FormControlSC
      colorVariant={colorVariant}
      hint={hint}
      isDisabled={isDisabled}
      isValid={isValid}
      label={label}
      validationPassedLabel={validationPassedLabel}
      validationWarningLabel={validationWarningLabel}
    >
      <TextareaSC
        $disableResize={disableResize}
        $isValid={isValid}
        rows={rows}
        {...rest}
        $colorVariant={colorVariant}
        disabled={isDisabled}
      />
    </FormControlSC>
  );
};
